import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, TextField, DialogActions, Button, Box, Typography, Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import request from '../../../helpers/myAxios'
import i18next from 'i18next';

import { Trans } from 'react-i18next';
import { useNotify } from '../../Notify/NotifyProvider';
import { useEffect } from 'react';
import { User } from '../../../types/User';
import { makeStyles } from '../../hooks/makeStyles';
import { Card } from '../../../types/Card';

export interface UserEditorModalProps {
    open?: boolean,
    onSave?: (object: Card) => void,
    onAbort?: () => void,
    editCard?: Card | null
}

interface IFormData {
    _id: string,
    code: string,
    ownerFirstName: string,
    ownerLastName: string,
    ownerAddress: string,
    ownerPhone: string,
    ownerEmail: string,
    ownerNote: string,
    status: 'enabled' | 'disabled',
    activationDate: Date | null,
    desactivationDate: Date | null
}

const UserEditorModal = (props: UserEditorModalProps) => {
    const notify = useNotify();
    const emptyFormData: IFormData = {
        _id: '',
        code: '',
        ownerFirstName: '',
        ownerLastName: '',
        ownerAddress: '',
        ownerPhone: '',
        ownerEmail: '',
        ownerNote: '',
        status: 'enabled',
        activationDate: null,
        desactivationDate: null
    }

    const [errors, setErrors] = useState<string[]>([]);

    const [formData, setFormData] = useState<IFormData>(emptyFormData);

    const isEdition = (props.editCard) ? true : false;

    const loadCard = (card: Card) => {
        if (card._id) {
            const formData: IFormData = {
                _id: card._id,
                code: card.code,
                ownerFirstName: card.owner?.firstName ?? '',
                ownerLastName: card.owner?.lastName ?? '',
                ownerAddress: card.owner?.address ?? '',
                ownerPhone: card.owner?.phone ?? '',
                ownerEmail: card.owner?.email ?? '',
                ownerNote: card.owner?.note ?? '',
                status: card.status ?? 'enabled',
                activationDate: null,
                desactivationDate: null
            };
            setFormData(formData);
        }

    }

    useEffect(() => {
        if (props.editCard)
            loadCard(props.editCard);
        else
            handleClear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props])

    const handleClear = () => {
        setErrors([]);
        setFormData(emptyFormData);
    }

    const handleAbort = () => {
        if (props.onAbort)
            props.onAbort();
        handleClear();
    }

    const handleSubmit = (e: React.FormEvent | null) => {
        const cardToPost: Card = {
            code: formData.code,
            status: formData.status
        };

        if (formData.ownerFirstName) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.firstName = formData.ownerFirstName;
        }
        if (formData.ownerLastName) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.lastName = formData.ownerLastName;
        }
        if (formData.ownerAddress) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.address = formData.ownerAddress;
        }
        if (formData.ownerPhone) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.phone = formData.ownerPhone;
        }
        if (formData.ownerEmail) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.email = formData.ownerEmail;
        }
        if (formData.ownerNote) {
            if (!cardToPost.owner)
                cardToPost.owner = {};
            cardToPost.owner.note = formData.ownerNote;
        }
        if (formData.activationDate)
            cardToPost.activationDate = formData.activationDate
        if (formData.desactivationDate)
            cardToPost.desactivationDate = formData.desactivationDate

        if (formData._id)
            cardToPost._id = formData._id;

        let newErrors: string[] = [];
        if (!formData.code)
            newErrors.push('code');

        if (newErrors.length) {
            setErrors(newErrors);
            return;
        }

        request()['post']('/card', { card: cardToPost }).then((res) => {
            let registeredCard = cardToPost;
            if (!registeredCard._id) {
                registeredCard._id = res.data._id;
            }
            if (props.onSave)
                props.onSave(registeredCard);

            notify.success(i18next.t(isEdition ? 'CardEditor.msgCardModified' : 'CardEditor.msgCardCreated'));
            handleClear();

        }).catch((err) => {
            notify.error(err.message);
        });

        if (e)
            e.preventDefault();
    }

    const handleChange = (event: any) => {
        const newFormData: any = { ...formData };
        if (event.target.id)
            newFormData[event.target.id] = event.target.value;
        else
            newFormData[event.target.name] = event.target.value;
        setFormData(newFormData);
    }

    const handleStatusCheckChange = (event: any) => {
        const newFormData: any = { ...formData };
        newFormData.status = event.target.checked ? 'enabled' : 'disabled';
        setFormData(newFormData);
    }

    const isError = (componentId: string, isRequired: boolean) => {
        if (isRequired)
            return errors.includes(componentId);
        return false;
    }

    return (
        <Dialog open={props.open ? true : false} fullWidth maxWidth='md'>
            <DialogTitle id="form-dialog-title">{isEdition ? <Trans i18nKey='CardEditor.titleEditCard' /> : <Trans i18nKey='CardEditor.titleCreateCard' />}</DialogTitle>
            <DialogContent sx={{ w: '100%', pt: 2 }}>
                <Grid component="form" container spacing={1} alignItems='flex-start' justifyContent="center">
                    <Grid container item xs={6}>
                        <Grid item xs={12}>
                            <Typography sx={{ p: 0 }} variant='overline' color='primary'><Trans i18nKey='CardEditor.general' /></Typography>
                            <Grid container spacing={1} sx={{ pt: 2 }}>
                                <Grid item xs={12}>
                                    <TextField id="code" fullWidth label={i18next.t('CardEditor.code')} size='small'
                                        value={formData.code} onChange={handleChange} variant="outlined" required error={isError('code', true)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="ownerFirstName" fullWidth label={i18next.t('CardEditor.firstName')} size='small'
                                        value={formData.ownerFirstName} onChange={handleChange} variant="outlined" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="ownerLastName" fullWidth label={i18next.t('CardEditor.lastName')} size='small'
                                        value={formData.ownerLastName} onChange={handleChange} variant="outlined" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="ownerEmail" fullWidth label={i18next.t('CardEditor.email')} size='small'
                                        value={formData.ownerEmail} onChange={handleChange} variant="outlined" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="ownerNote" fullWidth label={i18next.t('CardEditor.note')} size='small'
                                        value={formData.ownerNote} onChange={handleChange} variant="outlined" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField name="status" fullWidth label={i18next.t('CardEditor.status')} size='small'
                                        value={formData.status} onChange={handleChange} select variant='filled'>
                                        <MenuItem key='enabled' value='enabled'>{i18next.t('CardEditor.enabled')}</MenuItem>
                                        <MenuItem key='disabled' value='disabled'>{i18next.t('CardEditor.disabled')}</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='primary' onClick={handleSubmit}>{isEdition ? <Trans i18nKey='buttons.edit' /> : <Trans i18nKey='buttons.create' />}</Button>
                <Button variant='outlined' color='primary' onClick={handleAbort}><Trans i18nKey='buttons.cancel' /></Button>
            </DialogActions>
        </Dialog >
    )

}

export default UserEditorModal